import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { DEFAULT_SITE } from '../siteDefaults';
import { stripNewsHtmlToPlain } from '../newsHtml';
import {
  deleteNewsArticle,
  fetchSite,
  getToken,
  patchNewsSection,
  saveSite,
} from './api';
import { input, label } from './adminUi';

function ensureAdminSiteShape(site) {
  return {
    ...DEFAULT_SITE,
    ...site,
    news:
      site.news && typeof site.news === 'object' && Array.isArray(site.news.articles)
        ? site.news
        : DEFAULT_SITE.news,
    footer:
      site.footer && typeof site.footer === 'object' ? site.footer : DEFAULT_SITE.footer,
    products:
      site.products && typeof site.products === 'object' && Array.isArray(site.products?.items)
        ? site.products
        : DEFAULT_SITE.products,
  };
}

export default function NewsDashboard() {
  const navigate = useNavigate();
  const [site, setSite] = useState(null);
  const siteRef = useRef(null);
  const [msg, setMsg] = useState('');
  const [err, setErr] = useState('');
  const [saving, setSaving] = useState(false);

  siteRef.current = site;

  useEffect(() => {
    if (!getToken()) {
      navigate('/admin', { replace: true });
      return;
    }
    fetchSite()
      .then((data) => setSite(ensureAdminSiteShape(data)))
      .catch(() => setSite(ensureAdminSiteShape(JSON.parse(JSON.stringify(DEFAULT_SITE)))));
  }, [navigate]);

  function updateNews(field, value) {
    setSite((s) => ({ ...s, news: { ...s.news, [field]: value } }));
  }

  async function removeArticle(a, rowIndex) {
    if (!window.confirm('Remove this article from the site?')) return;
    setMsg('');
    setErr('');
    setSaving(true);
    try {
      if (a?.id) {
        const data = await deleteNewsArticle(a.id);
        setSite((s) => ensureAdminSiteShape({ ...s, news: data.news }));
        setMsg('Article removed.');
      } else {
        const s = siteRef.current;
        if (!s) throw new Error('No site state');
        const nextSite = {
          ...s,
          news: { ...s.news, articles: s.news.articles.filter((_, j) => j !== rowIndex) },
        };
        setSite(nextSite);
        const updated = await saveSite(ensureAdminSiteShape(nextSite));
        setSite(ensureAdminSiteShape(updated));
        setMsg('Article removed.');
      }
    } catch (e) {
      setErr(e.message || 'Remove failed');
      try {
        const data = await fetchSite();
        setSite(ensureAdminSiteShape(data));
      } catch {
        /* ignore */
      }
    } finally {
      setSaving(false);
    }
  }

  async function save() {
    const cur = siteRef.current;
    if (!cur?.news) return;
    setMsg('');
    setErr('');
    setSaving(true);
    try {
      const data = await patchNewsSection({
        sectionLabel: cur.news.sectionLabel,
        sectionTitle: cur.news.sectionTitle,
      });
      setSite((s) => ensureAdminSiteShape({ ...s, news: data.news }));
      setMsg('Saved.');
    } catch (e) {
      setErr(e.message || 'Save failed');
      try {
        const d = await fetchSite();
        setSite(ensureAdminSiteShape(d));
      } catch {
        /* ignore */
      }
    } finally {
      setSaving(false);
    }
  }

  if (!site) {
    return (
      <div className="flex min-h-[40vh] items-center justify-center text-neutral-600">Loading…</div>
    );
  }

  const articles = site.news.articles;

  return (
    <>
      <header className="z-10 flex shrink-0 flex-wrap items-center justify-between gap-3 border-b border-neutral-200 bg-white px-4 py-3 md:px-8">
        <div>
          <p className="text-xs font-medium text-neutral-500">Site content</p>
          <h2 className="text-lg font-bold text-neutral-900 md:text-xl">Latest news</h2>
        </div>
        <button
          type="button"
          onClick={save}
          disabled={saving}
          className="rounded-lg bg-brand-forest px-4 py-2 text-sm font-semibold text-white hover:opacity-90 disabled:opacity-50"
        >
          {saving ? 'Saving…' : 'Save section'}
        </button>
      </header>

      <main className="min-h-0 flex-1 overflow-x-hidden overflow-y-auto overscroll-y-contain px-4 py-6 md:px-8 md:py-8">
        {msg ? <p className="mb-4 text-sm text-green-700">{msg}</p> : null}
        {err ? <p className="mb-4 text-sm text-red-600">{err}</p> : null}

        <section className="rounded-2xl border border-neutral-200 bg-white p-6 shadow-sm">
          <h2 className="mb-4 text-lg font-bold text-neutral-900">News section</h2>
          <div className="grid gap-4 sm:grid-cols-2">
            <label className={label}>
              Section label
              <input
                className={input}
                value={site.news.sectionLabel}
                onChange={(e) => updateNews('sectionLabel', e.target.value)}
              />
            </label>
            <label className={`${label} sm:col-span-2`}>
              Section title
              <input
                className={input}
                value={site.news.sectionTitle}
                onChange={(e) => updateNews('sectionTitle', e.target.value)}
              />
            </label>
          </div>

          <div className="mt-8 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <h3 className="text-base font-semibold text-neutral-800">Articles</h3>
            <Link
              to="/admin/dashboard/news/new"
              className="inline-flex items-center justify-center rounded-lg bg-brand-forest px-4 py-2.5 text-sm font-semibold text-white shadow-sm hover:opacity-90"
            >
              + New article
            </Link>
          </div>

          {articles.length === 0 ? (
            <p className="mt-5 text-center text-sm text-neutral-500">
              No articles yet.{' '}
              <Link to="/admin/dashboard/news/new" className="font-medium text-brand-olive hover:underline">
                Create one
              </Link>
            </p>
          ) : (
            <div className="mt-5 overflow-x-auto rounded-xl border border-neutral-200 bg-white">
              <table className="w-full min-w-[min(100%,800px)] border-collapse text-left text-sm">
                <thead>
                  <tr className="border-b border-neutral-200 bg-neutral-50">
                    <th scope="col" className="w-24 px-3 py-3 text-xs font-semibold uppercase tracking-wide text-neutral-600">
                      Image
                    </th>
                    <th scope="col" className="px-3 py-3 text-xs font-semibold uppercase tracking-wide text-neutral-600">
                      Title
                    </th>
                    <th scope="col" className="w-[1%] whitespace-nowrap px-3 py-3 text-right text-xs font-semibold uppercase tracking-wide text-neutral-600">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {articles.map((a, i) => (
                    <tr key={a.id || i} className="border-b border-neutral-100 last:border-0 hover:bg-neutral-50/90">
                      <td className="align-middle px-3 py-2">
                        <div className="flex h-12 w-20 shrink-0 items-center justify-center overflow-hidden rounded-md bg-gradient-to-br from-neutral-100 to-neutral-200">
                          {a.image ? (
                            <img src={a.image} alt="" className="h-full w-full object-cover" />
                          ) : (
                            <span className="px-1 text-center text-[10px] font-medium text-neutral-400">No image</span>
                          )}
                        </div>
                      </td>
                      <td className="max-w-[min(28rem,40vw)] px-3 py-2 align-middle">
                        <span className="line-clamp-2 font-medium text-neutral-900">
                          {a.title?.trim() ? a.title : 'Untitled article'}
                        </span>
                        {a.excerpt ? (
                          <span className="mt-0.5 block line-clamp-2 text-xs text-neutral-600">
                            {stripNewsHtmlToPlain(a.excerpt)}
                          </span>
                        ) : null}
                      </td>
                      <td className="whitespace-nowrap px-3 py-2 align-middle text-right">
                        <div className="inline-flex flex-wrap items-center justify-end gap-2">
                          <Link
                            to={`/admin/dashboard/news/${encodeURIComponent(a.id)}/edit`}
                            className="rounded-lg border border-neutral-300 bg-white px-3 py-1.5 text-xs font-semibold text-neutral-800 hover:bg-neutral-50"
                          >
                            Edit
                          </Link>
                          <button
                            type="button"
                            className="rounded-lg border border-red-200 bg-white px-3 py-1.5 text-xs font-semibold text-red-700 hover:bg-red-50"
                            onClick={() => removeArticle(a, i)}
                          >
                            Remove
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      </main>
    </>
  );
}
